import React from "react";
import { useTheme } from "../../hooks/ThemeContext";
import { useOrderMode, ORDER_MODES } from "../../hooks/OrderModeContext";
import { getRemoteEnv } from "../../config/remoteEnv";
import StorePauseBanner from "../StorePause/StorePauseBanner";

// The fulfilment chooser — the first thing a customer sees for a store
// until a mode is in the URL. Big friendly buttons, only the modes the
// store has enabled. Also shown by the cart when the mode is missing.

const OrderModeSelect: React.FC = () => {
  const { theme } = useTheme();
  const { enabledModes, modeInfo, setMode } = useOrderMode();

  const accent = getRemoteEnv().themeOverrideAccentColor || theme.payButtonColor || "#4169E1";

  // ALL enabled modes — closed ones stay selectable (the menu is browsable
  // in a closed mode; only adding to cart is gated), marked with when they
  // reopen.
  const options = ORDER_MODES.filter((m) => enabledModes.includes(m.id));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: "60vh",
        padding: "32px 20px 60px",
        boxSizing: "border-box",
        backgroundColor: theme.backgroundColor,
        color: theme.textColor,
        textAlign: "center",
      }}
    >
      <StorePauseBanner style={{ maxWidth: "420px", marginBottom: "22px", textAlign: "left" }} />
      <h1 style={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-0.4px", margin: "0 0 6px" }}>
        How would you like to order?
      </h1>
      <p style={{ fontSize: "14px", opacity: 0.6, margin: "0 0 28px", lineHeight: 1.5 }}>
        You can change this at any time from the menu.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%", maxWidth: "420px" }}>
        {options.map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
              padding: "20px 22px",
              borderRadius: "18px",
              border: `1.5px solid ${theme.borderColor || "rgba(128,128,128,0.35)"}`,
              backgroundColor: theme.cardColor || "transparent",
              color: theme.textColor,
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
              transition: "transform 0.12s ease, border-color 0.12s ease",
              WebkitTapHighlightColor: "transparent",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.borderColor || "rgba(128,128,128,0.35)"; e.currentTarget.style.transform = "none"; }}
          >
            <span style={{
              width: "52px", height: "52px", borderRadius: "14px", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              backgroundColor: `${accent}1f`, color: accent,
            }}>
              <m.Icon size={28} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <span style={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-0.2px" }}>{m.label}</span>
              <span style={{ fontSize: "13px", opacity: 0.6 }}>{m.blurb}</span>
              {(() => {
                const status = modeInfo[m.id];
                if (!status) return null;
                return (
                  <span style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: status.open ? accent : "#e5484d",
                    opacity: status.open ? 0.85 : 1,
                  }}>
                    {status.label}
                  </span>
                );
              })()}
            </span>
            <span style={{ marginLeft: "auto", fontSize: "20px", opacity: 0.4 }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default OrderModeSelect;
